import type { ClientUpdateField, WebReportMeta, WebReportResponse } from '../types/webReport';

// The redesigned report is desktop-only; the constant keeps the server's
// viewMode whitelist + the dashboard's device split working unchanged.
const VIEW_MODE = 'desktop';

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

function url(path: string) {
  return `${API_URL}${path}`;
}

/**
 * Redeem an advisor access token (?at= on the link the dashboard opens) for an
 * access cookie, skipping the email-OTP gate. Returns true when the cookie was
 * set; false (stale/shared/invalid token) falls back to the normal gate.
 */
export async function redeemAdvisorToken(slug: string, at: string): Promise<boolean> {
  try {
    const res = await fetch(url(`/api/public/web-reports/${slug}/advisor-access`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ at }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Cheap "is my cookie still valid?" probe. Always returns 200 with
 * { valid: bool } so the viewer can decide whether to skip the email gate
 * without generating a noisy 401 in the console.
 */
export async function checkAccessStatus(slug: string): Promise<boolean> {
  try {
    const res = await fetch(url(`/api/public/web-reports/${slug}/access-status`), {
      credentials: 'include',
    });
    if (!res.ok) return false;
    const body = await res.json();
    return !!body?.valid;
  } catch {
    return false;
  }
}

export async function fetchMeta(slug: string): Promise<WebReportMeta> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/meta`), {
    credentials: 'include',
  });
  if (res.status === 404) throw new Error('not-found');
  if (res.status === 410) throw new Error('expired');
  if (!res.ok) throw new Error(`meta-failed:${res.status}`);
  return res.json();
}

/**
 * Stage 1 — request a verification code for the typed email. The server
 * returns 200 with a generic message regardless of whether the email is on
 * file (no enumeration). Throws on 410/429/5xx so the caller can show
 * specific errors.
 */
export async function requestCode(slug: string, email: string): Promise<void> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/request-code`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (res.status === 410) throw new Error('expired');
  if (res.status === 429) throw new Error('rate-limited');
  if (!res.ok) throw new Error(`request-code-failed:${res.status}`);
}

/**
 * Stage 2 — submit the 6-digit code from the user's inbox. On success the
 * server sets the 15-min HMAC access cookie; the caller can then fetch the
 * payload. Throws on 401/410/429/5xx.
 */
export async function verifyCode(slug: string, code: string): Promise<{ ok: true; expiresAt: number }> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/verify-code`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, viewMode: VIEW_MODE }),
  });
  if (res.status === 401) throw new Error('access-denied');
  if (res.status === 410) throw new Error('expired');
  if (res.status === 429) throw new Error('rate-limited');
  if (!res.ok) throw new Error(`verify-code-failed:${res.status}`);
  return res.json();
}

export async function fetchPayload(slug: string): Promise<WebReportResponse> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/payload`), {
    credentials: 'include',
  });
  if (res.status === 401) throw new Error('access-denied');
  if (res.status === 410) throw new Error('expired');
  if (!res.ok) throw new Error(`payload-failed:${res.status}`);
  return res.json();
}

/**
 * Payload fetch for the /:slug/print page — authenticates with the
 * short-lived print token minted by planscout-api (for the headless PDF
 * renderer) instead of the OTP access cookie.
 */
export async function fetchPayloadPrint(slug: string, pt: string): Promise<WebReportResponse> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/payload?pt=${encodeURIComponent(pt)}`));
  if (res.status === 401) throw new Error('access-denied');
  if (res.status === 410) throw new Error('expired');
  if (!res.ok) throw new Error(`payload-failed:${res.status}`);
  return res.json();
}

/**
 * Submit the client's edited Details fields + optional note. DIRECT APPLY:
 * the server writes whitelisted fields straight into the plan data, flags the
 * plan for a rerun, and emails the advisor the diff. Cookie-gated + rate
 * limited server-side.
 */
export async function submitClientUpdate(
  slug: string,
  body: { fields: ClientUpdateField[]; note?: string | null }
): Promise<void> {
  const res = await fetch(url(`/api/public/web-reports/${slug}/client-updates`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) throw new Error('access-denied');
  if (res.status === 429) throw new Error('rate-limited');
  if (!res.ok) {
    let message = `update-failed:${res.status}`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {
      /* keep generic message */
    }
    throw new Error(message);
  }
}
